import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  createWorkspaceInvitation,
  fetchWorkspaceInvitations,
  fetchWorkspaceMembers,
  removeWorkspaceMember,
  resendWorkspaceInvitation,
  revokeWorkspaceInvitation,
} from '@/api/team/teamApi';
import type { WorkspaceInvitationInput } from '@/api/team/teamApi.types';
import type { Invitation, InvitationWithLink } from '@/api/invitations/invitationsApi.types';
import type { PaginatedResult } from '@/api/pagination.types';
import type { TenantMember } from '@/api/tenants/tenantsApi.types';
import { ADMIN_LIST_PAGE_SIZE } from '@/constants/adminPagination';
import {
  buildWorkspaceInvitationsQueryKey,
  buildWorkspaceTeamMembersQueryKey,
} from '@/constants/queryKeys';
import { useAuth } from '@/hooks/useAuth';

export function useWorkspaceMembersQuery(): UseQueryResult<TenantMember[], Error> {
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: buildWorkspaceTeamMembersQueryKey(activeTenantId),
    queryFn: fetchWorkspaceMembers,
  });
}

export function useWorkspaceInvitationsQuery(
  page: number
): UseQueryResult<PaginatedResult<Invitation>, Error> {
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: buildWorkspaceInvitationsQueryKey(activeTenantId, page),
    queryFn: function loadInvitations() {
      return fetchWorkspaceInvitations(page, ADMIN_LIST_PAGE_SIZE);
    },
    placeholderData: keepPreviousData,
  });
}

export function useCreateWorkspaceInvitationMutation(): UseMutationResult<
  InvitationWithLink,
  Error,
  WorkspaceInvitationInput
> {
  const queryClient = useQueryClient();
  const { activeTenantId } = useAuth();
  return useMutation({
    mutationFn: function submitInvitation(input: WorkspaceInvitationInput) {
      return createWorkspaceInvitation(input);
    },
    onSuccess: function invalidateInvitations() {
      void queryClient.invalidateQueries({
        queryKey: ['workspaceInvitations', activeTenantId],
      });
    },
  });
}

export function useResendWorkspaceInvitationMutation(): UseMutationResult<
  InvitationWithLink,
  Error,
  string
> {
  const queryClient = useQueryClient();
  const { activeTenantId } = useAuth();
  return useMutation({
    mutationFn: function submitResend(invitationId: string) {
      return resendWorkspaceInvitation(invitationId);
    },
    onSuccess: function invalidateInvitations() {
      void queryClient.invalidateQueries({
        queryKey: ['workspaceInvitations', activeTenantId],
      });
    },
  });
}

export function useRevokeWorkspaceInvitationMutation(): UseMutationResult<Invitation, Error, string> {
  const queryClient = useQueryClient();
  const { activeTenantId } = useAuth();
  return useMutation({
    mutationFn: function submitRevoke(invitationId: string) {
      return revokeWorkspaceInvitation(invitationId);
    },
    onSuccess: function invalidateInvitations() {
      void queryClient.invalidateQueries({
        queryKey: ['workspaceInvitations', activeTenantId],
      });
    },
  });
}

export function useRemoveWorkspaceMemberMutation(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  const { activeTenantId } = useAuth();
  return useMutation({
    mutationFn: function submitRemove(userId: string) {
      return removeWorkspaceMember(userId);
    },
    onSuccess: function invalidateMembers() {
      void queryClient.invalidateQueries({
        queryKey: buildWorkspaceTeamMembersQueryKey(activeTenantId),
      });
    },
  });
}
